/**
 * The pages nobody signs in to reach.
 *
 * <p>
 * A customer accepting a quotation, and whoever is checking an acceptance reference, have
 * no ARMS account and never will. Both pages are routed outside the auth guard already -
 * this is the other half of that: the session restore must not run on them either.
 * </p>
 *
 * Restoring asks ARMS whether this browser is already signed in, and a browser that is gets
 * a full-page redirect for its answer. On a customer page that redirect is pure cost: it
 * kills the quotation request already in flight, which surfaces as "Could not reach the
 * server" for as long as the round trip takes, and it is a trip to an identity provider for
 * a page that will not consult the result.
 *
 * See docs/adr/0004-customer-acceptance-link.md.
 */
/**
 * `bare` is whether the address means anything without something after it.
 *
 * `/verify` is a page in its own right - the box you paste a reference into - so it counts
 * on its own. `/accept` is not: the token is the whole capability, and the bare path is a
 * route that does not exist. Saying so here keeps a 404 inside the application, where a
 * signed-in employee who mistyped gets the app rather than a customer's dead end.
 */
const ANONYMOUS_ROUTES = [
  { base: '/accept', bare: false },
  { base: '/verify', bare: true },

  // A published contact form. The slug is the whole address, like the token is.
  { base: '/f', bare: false },
] as const

/** Whether this path is one of the pages that must never wait on, or trigger, a sign-in. */
export function isAnonymousPath(pathname: string): boolean {
  // Trailing slash dropped and case folded, so /Verify/ and /verify answer the same.
  const path = pathname.toLowerCase().replace(/\/+$/, '') || '/'

  return ANONYMOUS_ROUTES.some(({ base, bare }) =>
    // Segment-wise, never a bare startsWith: "/verifying" is not the verification page, and
    // a route added later called "/accepted" is not the customer's.
    path.startsWith(`${base}/`) || (bare && path === base))
}
